import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { TrendingUp, Calendar, Activity, QrCode, Barcode, Search } from 'lucide-react';
import type { ScanRecord } from '../../../shared/types/index.js';

interface Scan30DayChartProps {
  history: ScanRecord[];
}

export const Scan30DayChart: React.FC<Scan30DayChartProps> = ({ history }) => {
  // Aggregate scan counts per day over the last 30 days
  const { chartData, total30Days, peakCount, peakDate, methodBreakdown } = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const daysMap = new Map<string, { dateStr: string; label: string; count: number; barcode: number; qr: number; search: number }>();

    // Initialize 30 days (from 29 days ago to today)
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(today.getDate() - i);
      const dateKey = d.toISOString().split('T')[0];
      const dayLabel = d.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });
      
      daysMap.set(dateKey, {
        dateStr: dateKey,
        label: dayLabel,
        count: 0,
        barcode: 0,
        qr: 0,
        search: 0,
      });
    }

    let barcodeTotal = 0;
    let qrTotal = 0;
    let searchTotal = 0;

    // Populate counts from scan history
    history.forEach((scan) => {
      const scanDate = new Date(scan.scannedAt);
      const key = scanDate.toISOString().split('T')[0];

      if (daysMap.has(key)) {
        const item = daysMap.get(key)!;
        item.count += 1;

        if (scan.scanMethod === 'barcode_scanner') {
          item.barcode += 1;
          barcodeTotal += 1;
        } else if (scan.scanMethod === 'qr_scanner') {
          item.qr += 1;
          qrTotal += 1;
        } else {
          item.search += 1;
          searchTotal += 1;
        }
      }
    });

    const data = Array.from(daysMap.values());
    let total = 0;
    let max = 0;
    let maxDateLabel = '-';

    data.forEach((d) => {
      total += d.count;
      if (d.count > max) {
        max = d.count;
        maxDateLabel = d.label;
      }
    });

    return {
      chartData: data,
      total30Days: total,
      peakCount: max,
      peakDate: maxDateLabel,
      methodBreakdown: {
        barcode: barcodeTotal,
        qr: qrTotal,
        search: searchTotal,
      },
    };
  }, [history]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-2xs space-y-4">
      {/* Chart Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-indigo-50 border border-indigo-100 text-indigo-600">
              <TrendingUp className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-base">
                Tren Pemindaian 30 Hari Terakhir
              </h3>
              <p className="text-xs text-slate-500">
                Visualisasi frekuensi aktivitas pemindaian & pencarian UIBN harian.
              </p>
            </div>
          </div>
        </div>

        {/* Quick KPI Badges */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-indigo-600" />
            <span className="text-slate-500">Total (30 Hr):</span>
            <strong className="text-slate-900 font-bold">{total30Days} scan</strong>
          </div>

          <div className="px-3 py-1.5 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-slate-500">Puncak ({peakDate}):</span>
            <strong className="text-slate-900 font-bold">{peakCount} scan</strong>
          </div>
        </div>
      </div>

      {/* Chart Visual Surface */}
      <div className="w-full h-56 pt-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorScanGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={20}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const d = payload[0].payload;
                  return (
                    <div className="p-3 bg-slate-900 text-white rounded-xl text-xs shadow-xl border border-slate-800 space-y-1.5 min-w-[150px]">
                      <div className="font-bold text-slate-200 border-b border-slate-800 pb-1 flex items-center justify-between">
                        <span>{d.label}</span>
                        <span className="text-indigo-400 font-mono font-extrabold">{d.count} Scan</span>
                      </div>
                      <div className="space-y-1 text-[11px] text-slate-300">
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-1 text-slate-400">
                            <Barcode className="w-3 h-3 text-indigo-400" /> Barcode:
                          </span>
                          <strong className="font-mono text-white">{d.barcode}</strong>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-1 text-slate-400">
                            <QrCode className="w-3 h-3 text-purple-400" /> QR Code:
                          </span>
                          <strong className="font-mono text-white">{d.qr}</strong>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-1 text-slate-400">
                            <Search className="w-3 h-3 text-emerald-400" /> UIBN Search:
                          </span>
                          <strong className="font-mono text-white">{d.search}</strong>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke="#4f46e5"
              strokeWidth={2.5}
              fillOpacity={1}
              fill="url(#colorScanGrad)"
              activeDot={{ r: 5, fill: '#4f46e5', stroke: '#ffffff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Method Breakdown Footer Pills */}
      <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between text-xs text-slate-500 gap-2">
        <span className="text-[11px]">Metode Pemindaian Terpopuler (30 Hari):</span>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-[11px] font-semibold border border-indigo-100">
            <Barcode className="w-3 h-3" /> Barcode ({methodBreakdown.barcode})
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-50 text-purple-700 text-[11px] font-semibold border border-purple-100">
            <QrCode className="w-3 h-3" /> QR Code ({methodBreakdown.qr})
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-[11px] font-semibold border border-emerald-100">
            <Search className="w-3 h-3" /> UIBN Search ({methodBreakdown.search})
          </span>
        </div>
      </div>
    </div>
  );
};
